/**
 * Mirror outbound `command/*` envelopes to the backend audit log.
 *
 * Phase 1B-D: every state-changing event on the platform hashes into a
 * per-session SHA-256 chain. Camera commands originate on the frontend's
 * data channel (LiveKit), so there's no server-side write site to hook into
 * — we POST them to `/sessions/{id}/commands/log` after the bus publishes.
 *
 * Best-effort by design: a network failure here must NOT prevent the
 * user-facing command from working. Worst case the chain has a small gap;
 * the verify endpoint flags it but the operator can still drive the
 * inspection.
 *
 * Auth: uses the shared `api` axios instance (Bearer JWT) on the operator
 * side; the field side passes its `fieldSessionToken` through so the
 * X-Field-Session-Token header is attached.
 */
import axios from "axios";

import { ApiPaths, api } from "./api";
import type { AuditMirror, Envelope } from "./commandBus";


export function makeAuditMirror(
  sessionId: string,
  fieldSessionToken?: string,
): AuditMirror {
  return async (env: Envelope) => {
    if (env.topic !== "command") return;
    const body = {
      topic: env.topic,
      type: env.type,
      payload: env.payload,
      envelope_msg_id: env.id,
    };
    if (fieldSessionToken) {
      const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
      await axios.post(
        `${apiBase}${ApiPaths.sessionCommandLog(sessionId)}`,
        body,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Field-Session-Token": fieldSessionToken,
          },
        },
      );
    } else {
      await api.post(ApiPaths.sessionCommandLog(sessionId), body);
    }
  };
}
